/**
 * @typedef {{ id: number, code: string, displayName: string }} CompanySource
 *
 * @param {number | string | null | undefined} companySourceId
 * @param {CompanySource[]} companySources
 * @returns {CompanySource | null}
 */
function resolveCompanySource(companySourceId, companySources) {
    if (companySourceId == null) {
        return null;
    }

    const numericId = Number(companySourceId);
    const match = companySources.find((source) => source.id === numericId);

    if (match) {
        return match;
    }

    return {
        id: numericId,
        code: `source_${numericId}`,
        displayName: `Source ${numericId}`,
    };
}

/**
 * @param {object[]} maps
 * @param {CompanySource[]} companySources
 * @param {string} [quantityField='expectedCaseQuantity']
 * @returns {Array<{ companySource: CompanySource, numCases: number }>}
 */
function buildSuggestedAllocations(maps, companySources, quantityField = 'expectedCaseQuantity') {
    if (!maps?.length) {
        return [];
    }

    /** @type {Map<number, number>} */
    const casesBySourceId = new Map();

    for (const map of maps) {
        const { companySourceId } = map;
        if (companySourceId != null) {
            const numericId = Number(companySourceId);
            const quantity = Number(map[quantityField]) || 0;
            casesBySourceId.set(numericId, (casesBySourceId.get(numericId) ?? 0) + quantity);
        }
    }

    return [...casesBySourceId.entries()]
        .map(([sourceId, numCases]) => {
            const companySource = resolveCompanySource(sourceId, companySources);
            if (!companySource) {
                return null;
            }
            return { companySource, numCases };
        })
        .filter(Boolean)
        .sort((a, b) => a.companySource.displayName.localeCompare(b.companySource.displayName));
}

/**
 * @param {object[]} maps
 * @param {CompanySource[]} companySources
 * @returns {CompanySource[]}
 */
function buildCompanySourceOptionsFromMaps(maps, companySources) {
    if (!maps?.length) {
        return [];
    }

    const sourceIds = new Set(
        maps
            .map((map) => map.companySourceId)
            .filter((id) => id != null)
            .map((id) => Number(id)),
    );

    return companySources.filter((source) => sourceIds.has(source.id));
}

export { buildCompanySourceOptionsFromMaps, buildSuggestedAllocations, resolveCompanySource };
